import { memo, useLayoutEffect, useRef } from "react";
import { Text } from "recharts";

/** Matches {@link RotatedCategoryAxisTick} / chart view X-axis tick styling. */
const VALUE_AXIS_TICK_CLASS = "fill-muted-foreground text-xxs";

export interface MeasurableValueAxisTickProps {
  x: number | string;
  y: number | string;
  payload: {
    value: string | number;
    coordinate: number;
    index: number;
  };
  valueFormatter?: (value: number) => string;
  onWidthChange?: (width: number) => void;
  /** @default 'end' — use `'start'` for a right-oriented Y axis. */
  textAnchor?: "start" | "end";
}

/** Right-aligned numeric Y-axis tick; reports label width for axis sizing. */
export const MeasurableValueAxisTick = memo(
  ({
    x,
    y,
    payload,
    valueFormatter,
    onWidthChange,
    textAnchor = "end",
  }: MeasurableValueAxisTickProps) => {
    const textRef = useRef<SVGTextElement>(null);

    useLayoutEffect(() => {
      if (textRef.current && onWidthChange) {
        onWidthChange(textRef.current.getBBox().width);
      }
    }, [payload.value, onWidthChange]);

    return (
      <Text
        x={x}
        y={y}
        textAnchor={textAnchor}
        verticalAnchor="middle"
        className={VALUE_AXIS_TICK_CLASS}
        ref={textRef}
      >
        {valueFormatter && typeof payload.value === "number"
          ? valueFormatter(payload.value)
          : payload.value}
      </Text>
    );
  },
);

MeasurableValueAxisTick.displayName = "MeasurableValueAxisTick";
